import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card } from "@/components/admin/ui";
import StepPersonal from "@/components/wizard/StepPersonal";
import StepAddresses, { emptyAddress, Address } from "@/components/wizard/StepAddresses";

const today = () => new Date().toISOString().slice(0, 10);

export default function ManualCase() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<any[]>([]);
  const [orgId, setOrgId] = useState("");
  const [location, setLocation] = useState("");
  const [via, setVia] = useState("email");
  const [received, setReceived] = useState(today());
  const [note, setNote] = useState("");
  const [matter, setMatter] = useState("");
  const [personal, setPersonal] = useState<Record<string, string>>({});
  const [addrs, setAddrs] = useState<Address[]>([emptyAddress()]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/organizations").then((r) => setOrgs(r.data)); }, []);

  const org = orgs.find((o) => o.id === orgId);

  const save = async () => {
    if (!orgId) return toast.error("Choose the organization");
    if (org?.form_type === "dow" && !location) return toast.error("Choose the parish or school");
    if (!personal.first_name || !personal.last_name || !personal.dob) return toast.error("First name, last name and date of birth are required");
    const filled = addrs.filter((a) => [a.street, a.city, a.state, a.zip, a.county, a.province, a.other_country].some((v) => (v || "").trim()));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cases/manual", { org_id: orgId, received_via: via, received_at: new Date(received).toISOString(), personal, addresses: filled, billing_note: note, client_matter: matter, location });
      toast.success("Applicant entered and searches created");
      navigate(`/admin/cases/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl">
      <Link to="/admin/cases" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#64748B] hover:text-[#1B365D]"><ArrowLeft className="h-4 w-4" /> Case queue</Link>
      <PageHeader title="Enter an applicant manually" subtitle="For applications received by email, mail or fax without a code. These are billed to the organization at the flat manual-entry fee." />
      <div className="space-y-6">
        <Card title="Received from" testId="manual-received-card">
          <div className="grid gap-5 p-6 sm:grid-cols-3">
            <label className="block sm:col-span-3"><span className="field-label">Organization</span>
              <select className="field-input" value={orgId} onChange={(e) => { setOrgId(e.target.value); setLocation(""); }} data-testid="manual-org-select">
                <option value="">Select…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}{o.reference_no ? ` (${o.reference_no})` : ""}</option>)}
              </select>
            </label>
            {org?.form_type === "dow" && (
              <label className="block sm:col-span-3"><span className="field-label">Parish / school</span>
                <select className="field-input" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="manual-location-select">
                  <option value="">Select…</option>
                  {(org.locations || []).map((l: string) => <option key={l}>{l}</option>)}
                </select>
              </label>
            )}
            <label className="block"><span className="field-label">Received by</span>
              <select className="field-input" value={via} onChange={(e) => setVia(e.target.value)} data-testid="manual-via-select">
                <option value="email">Email</option><option value="mail">Mail</option><option value="fax">Fax</option><option value="other">Other</option>
              </select>
            </label>
            <label className="block"><span className="field-label">Date received</span><input type="date" className="field-input" value={received} onChange={(e) => setReceived(e.target.value)} data-testid="manual-received-date" /></label>
            <label className="block"><span className="field-label">Billing note (optional)</span><input className="field-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="manual-billing-note" /></label>
            <label className="block"><span className="field-label">Client / matter reference (optional, prints on report)</span><input className="field-input" maxLength={200} placeholder="e.g. Smith v. Jones / File 24-118" value={matter} onChange={(e) => setMatter(e.target.value)} data-testid="manual-client-matter" /></label>
          </div>
        </Card>
        <Card title="Applicant" testId="manual-personal-card"><div className="p-6"><StepPersonal p={personal} onChange={setPersonal} /></div></Card>
        <Card title="Address history (optional)" testId="manual-addresses-card"><div className="p-6">
          <p className="mb-4 text-sm text-[#64748B]" data-testid="manual-addresses-hint">Enter whatever you have: a full address, just a town and state, or leave it blank. Out-of-state court searches are added for any state you enter.</p>
          <StepAddresses addrs={addrs} onChange={setAddrs} /></div></Card>
        <button className="btn-primary" disabled={busy} onClick={save} data-testid="manual-save-button">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Create case</button>
      </div>
    </div>
  );
}
